import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';

const GalleryManager = () => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingImage, setEditingImage] = useState(null);
  const [formData, setFormData] = useState({
    image_url: '',
    title: '',
    description: '',
    display_order: 0
  });

  useEffect(() => {
    fetchImages();
  }, []);

  const fetchImages = async () => {
    try {
      // Try to get from localStorage first
      const storedImages = localStorage.getItem('gallery_images');
      if (storedImages) {
        setImages(JSON.parse(storedImages));
      } else {
        // Initialize with default images
        const defaultImages = [
          { 
            id: 1, 
            image_url: '/images/truehome1.jpeg', 
            title: 'TrueHome Property 1', 
            description: 'Premium property showcase',
            display_order: 1,
            is_active: true,
            created_at: new Date().toISOString()
          },
          { 
            id: 2, 
            image_url: '/images/truehome3.jpeg', 
            title: 'TrueHome Property 2', 
            description: 'Luxury residential development',
            display_order: 2,
            is_active: true,
            created_at: new Date().toISOString()
          },
          { 
            id: 3, 
            image_url: '/images/truehome4.jpeg', 
            title: 'TrueHome Property 3', 
            description: 'Modern architecture design',
            display_order: 3,
            is_active: true,
            created_at: new Date().toISOString()
          },
          { 
            id: 4, 
            image_url: '/images/truehome.jpg', 
            title: 'TrueHome Property 4', 
            description: 'Scenic property location',
            display_order: 4,
            is_active: true,
            created_at: new Date().toISOString()
          }
        ];
        localStorage.setItem('gallery_images', JSON.stringify(defaultImages));
        setImages(defaultImages);
      }
    } catch (error) {
      console.error('Failed to fetch gallery images:', error);
      setImages([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const storedImages = localStorage.getItem('gallery_images');
      const currentImages = storedImages ? JSON.parse(storedImages) : [];
      
      if (editingImage) {
        // Update existing image
        const updatedImages = currentImages.map(img => 
          img.id === editingImage.id ? { ...img, ...formData } : img
        );
        localStorage.setItem('gallery_images', JSON.stringify(updatedImages));
        toast.success('Image updated!');
      } else {
        // Add new image
        const newImage = {
          id: Date.now(),
          ...formData,
          is_active: true,
          created_at: new Date().toISOString()
        };
        currentImages.push(newImage);
        localStorage.setItem('gallery_images', JSON.stringify(currentImages));
        toast.success('Image added!');
      }
      
      setShowForm(false);
      setEditingImage(null);
      setFormData({ image_url: '', title: '', description: '', display_order: 0 });
      fetchImages();
    } catch (error) {
      toast.error('Error saving image');
    }
  };

  const handleEdit = (image) => {
    setEditingImage(image);
    setFormData({
      image_url: image.image_url,
      title: image.title || '',
      description: image.description || '',
      display_order: image.display_order || 0
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this image?')) return;

    try {
      const storedImages = localStorage.getItem('gallery_images');
      const currentImages = storedImages ? JSON.parse(storedImages) : [];
      const updatedImages = currentImages.filter(img => img.id !== id);
      localStorage.setItem('gallery_images', JSON.stringify(updatedImages));
      toast.success('Image deleted!');
      fetchImages();
    } catch (error) {
      toast.error('Error deleting image');
    }
  };

  const handleToggleActive = async (image) => {
    try {
      const storedImages = localStorage.getItem('gallery_images');
      const currentImages = storedImages ? JSON.parse(storedImages) : [];
      const updatedImages = currentImages.map(img => 
        img.id === image.id ? { ...img, is_active: !img.is_active } : img
      );
      localStorage.setItem('gallery_images', JSON.stringify(updatedImages));
      toast.success(image.is_active ? 'Image hidden' : 'Image activated');
      fetchImages();
    } catch (error) {
      toast.error('Error updating image');
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData({ ...formData, image_url: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };

  if (loading) return <div className="admin-loading">Loading gallery...</div>;

  return (
    <div className="gallery-manager">
      <div className="gallery-header">
        <h2>Gallery Management</h2>
        <button 
          className="btn-luxhom"
          onClick={() => {
            setShowForm(!showForm);
            setEditingImage(null);
            setFormData({ image_url: '', title: '', description: '', display_order: 0 });
          }}
        >
          {showForm ? 'Cancel' : '+ Add Image'}
        </button>
      </div>

      {showForm && (
        <form className="gallery-form" onSubmit={handleSubmit}>
          <h3>{editingImage ? 'Edit Image' : 'Add New Image'}</h3>
          
          <div className="form-group">
            <label>Image Upload</label>
            <input
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              className="form-control"
            />
            <small>Or enter image URL below</small>
          </div>

          <div className="form-group">
            <label>Image URL *</label>
            <input
              type="text"
              value={formData.image_url}
              onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
              required
              className="form-control"
              placeholder="https://example.com/image.jpg or base64"
            />
          </div>

          {formData.image_url && (
            <div className="image-preview">
              <img src={formData.image_url} alt="Preview" />
            </div>
          )}

          <div className="form-group">
            <label>Title</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="form-control"
              placeholder="Image title"
            />
          </div>

          <div className="form-group">
            <label>Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="form-control"
              placeholder="Image description"
              rows="3"
            />
          </div>

          <div className="form-group">
            <label>Display Order</label>
            <input
              type="number"
              value={formData.display_order}
              onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) })}
              className="form-control"
              placeholder="0"
            />
          </div>

          <button type="submit" className="btn-luxhom">
            {editingImage ? 'Update Image' : 'Add Image'}
          </button>
        </form>
      )}

      <div className="gallery-grid">
        {images.map((image) => (
          <div key={image.id} className={`gallery-card ${!image.is_active ? 'inactive' : ''}`}>
            <div className="gallery-image">
              <img src={image.image_url} alt={image.title || 'Gallery image'} />
              {!image.is_active && <div className="inactive-badge">Hidden</div>}
            </div>
            <div className="gallery-info">
              <h4>{image.title || 'Untitled'}</h4>
              <p>{image.description || 'No description'}</p>
              <span className="order-badge">Order: {image.display_order}</span>
            </div>
            <div className="gallery-actions">
              <button onClick={() => handleEdit(image)} className="btn-edit">
                <i className="fas fa-edit"></i> Edit
              </button>
              <button onClick={() => handleToggleActive(image)} className="btn-toggle">
                <i className={`fas fa-eye${image.is_active ? '-slash' : ''}`}></i>
                {image.is_active ? 'Hide' : 'Show'}
              </button>
              <button onClick={() => handleDelete(image.id)} className="btn-delete">
                <i className="fas fa-trash"></i> Delete
              </button>
            </div>
          </div>
        ))}
      </div>

      {images.length === 0 && (
        <div className="empty-state">
          <i className="fas fa-images"></i>
          <p>No gallery images yet. Add your first image!</p>
        </div>
      )}
    </div>
  );
};

export default GalleryManager;
